import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar, Users, MapPin, Clock, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SEO } from '@/components/SEO';

const FamilyRetreat = () => {
  const navigate = useNavigate();

  const handleBooking = () => {
    navigate('/');
    setTimeout(() => window.scrollTo(0, 0), 100);
  };

  const handleGoBack = () => {
    navigate('/#upcoming-experiences');
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": "Family Retreat - Reconnect, Unwind, Create Memories",
    "description": "Reconnect, unwind, and create lasting memories together in nature's embrace. A mindful retreat for families with children 6+.",
    "startDate": "2024-03-15",
    "endDate": "2024-03-17",
    "location": {
      "@type": "Place",
      "name": "Mystic Forest Sanctuary",
      "address": "Beskid Wyspowy, Poland"
    },
    "organizer": {
      "@type": "Organization",
      "name": "Mystic Forest",
      "url": "https://mysticforest.club"
    },
    "offers": {
      "@type": "Offer",
      "price": "890",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock"
    }
  };

  return (
    <>
      <SEO
        title="Family Retreat - Reconnect & Create Memories in Nature"
        description="A 3-day mindful retreat for families with children 6+. Reconnect through nature activities, mindful cooking, and storytelling in our forest sanctuary."
        canonicalUrl="https://mysticforest.club/experiences/family-retreat"
        jsonLd={jsonLd}
      />
      
      <div className="min-h-screen bg-background">
        <Navigation />
      
      {/* Go Back Button */}
      <div className="pt-20 pb-4">
        <div className="max-w-6xl mx-auto px-6">
          <Button 
            variant="ghost" 
            onClick={handleGoBack}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Experiences
          </Button>
        </div>
      </div>
      
      {/* Hero Section */}
      <section className="relative py-32">
        <div className="absolute inset-0">
          <img 
            src="/lovable-uploads/f9b7c860-fa84-4238-ab53-54bc83a8f0c2.png" 
            alt="Family enjoying forest retreat activities together"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        
        <div className="relative max-w-4xl mx-auto px-6 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-serif font-light mb-6">
            Family Retreat
          </h1>
          <p className="text-xl md:text-2xl mb-8 opacity-90">
            Reconnect, unwind, and create lasting memories together
          </p>
          <Button size="lg" className="px-8 py-4 text-lg" onClick={handleBooking}>
            Reserve Your Spot
          </Button>
        </div>
      </section>

      {/* Quick Info */}
      <section className="py-16 bg-card">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div className="flex flex-col items-center">
              <Calendar className="w-8 h-8 mb-4 text-accent" />
              <h3 className="font-semibold mb-2">When</h3>
              <p className="text-muted-foreground">March 15-17, 2024</p>
            </div>
            <div className="flex flex-col items-center">
              <MapPin className="w-8 h-8 mb-4 text-accent" />
              <h3 className="font-semibold mb-2">Where</h3>
              <p className="text-muted-foreground">Mystic Forest Sanctuary</p>
            </div>
            <div className="flex flex-col items-center">
              <Users className="w-8 h-8 mb-4 text-accent" />
              <h3 className="font-semibold mb-2">For Whom</h3>
              <p className="text-muted-foreground">Families with children 6+</p>
            </div>
            <div className="flex flex-col items-center">
              <Clock className="w-8 h-8 mb-4 text-accent" />
              <h3 className="font-semibold mb-2">Duration</h3>
              <p className="text-muted-foreground">3 Days, 2 Nights</p>
            </div>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-serif font-light mb-8 text-center">
            A Sacred Space for Family Connection
          </h2>
          <div className="prose prose-lg max-w-none text-muted-foreground">
            <p className="mb-6">
              In our fast-paced world, families often struggle to find quality time together. Our Family Retreat offers a sanctuary where parents and children can disconnect from digital distractions and reconnect with each other in nature's embrace.
            </p>
            <p className="mb-6">
              Set in the heart of our pristine forest sanctuary, this retreat combines mindful activities, nature exploration, and meaningful conversations designed to strengthen family bonds and create lasting memories.
            </p>
          </div>
        </div>
      </section>

      {/* Schedule */}
      <section className="py-16 bg-card">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-serif font-light mb-12 text-center">
            What to Expect
          </h2>
          <div className="space-y-8">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span className="w-8 h-8 bg-accent text-accent-foreground rounded-full flex items-center justify-center text-sm font-semibold">1</span>
                  Friday Evening - Arrival & Sacred Circle
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Welcome ceremony, nature walk, and family sharing circle around the fire. Set intentions for your journey together.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span className="w-8 h-8 bg-accent text-accent-foreground rounded-full flex items-center justify-center text-sm font-semibold">2</span>
                  Saturday - Forest Adventures & Mindful Activities
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li>Morning meditation and family yoga session</li>
                  <li>Forest treasure hunt and nature craft workshops</li>
                  <li>Mindful cooking workshop - preparing lunch together</li>
                  <li>Storytelling under the stars</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span className="w-8 h-8 bg-accent text-accent-foreground rounded-full flex items-center justify-center text-sm font-semibold">3</span>
                  Sunday - Integration & Commitment
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Family visioning session, creating family mission statements, and closing ceremony with commitment to continue your journey at home.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Investment */}
      <section className="py-16">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-serif font-light mb-8">Investment</h2>
          <Card className="p-8">
            <div className="text-4xl font-semibold mb-4">$890</div>
            <p className="text-muted-foreground mb-6">Per family (up to 4 members)</p>
            <div className="text-sm text-muted-foreground mb-8">
              <p>Includes accommodation, all meals, activities, and materials</p>
              <p>Additional family members: $150 each</p>
            </div>
            <Button size="lg" className="w-full" onClick={handleBooking}>
              Book Your Family Experience
            </Button>
          </Card>
        </div>
      </section>

        <Footer />
      </div>
    </>
  );
};

export default FamilyRetreat;